import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../services/supabaseClient';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [state, setState] = useState({
        user: null,
        role: null,
        assignedEvents: [], // event rows an event_admin may manage (empty for other roles)
        loading: true,
        error: null
    });

    // Use refs for closure-safe state access
    const stateRef = useRef(state);
    const initializedRef = useRef(false);

    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    const updateState = useCallback((updates) => {
        setState(prev => ({ ...prev, ...updates }));
    }, []);

    const fetchUserRole = useCallback(async (userId) => {
        if (!userId) return 'user';

        try {
            const { data, error } = await supabase
                .from('app_users')
                .select('role')
                .eq('id', userId)
                .single();

            if (error) {
                console.error('[Auth] Error fetching role:', error);
                return 'user';
            }
            return data?.role || 'user';
        } catch (err) {
            console.error('[Auth] Role fetch exception:', err);
            return 'user';
        }
    }, []);

    // For event_admins, load the events they're assigned to. Other roles get [].
    // Failures degrade to [] (no assigned events) rather than blocking login.
    const fetchAssignedEvents = useCallback(async (role) => {
        if (role !== 'event_admin') return [];
        try {
            const { data, error } = await supabase
                .from('event_admin_assignments')
                .select('event_id, events(id, code, short_name, name, is_archived)');
            if (error) {
                console.error('[Auth] Error fetching assigned events:', error);
                return [];
            }
            return (data || []).map(r => r.events).filter(Boolean);
        } catch (err) {
            console.error('[Auth] Assigned events fetch exception:', err);
            return [];
        }
    }, []);

    useEffect(() => {
        let mounted = true;

        // Start auto refresh
        supabase.auth.startAutoRefresh();

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (!mounted) return;

            // INITIAL_SESSION is the primary event for page load/refresh
            if (event === 'INITIAL_SESSION') {
                if (session?.user) {
                    const role = await fetchUserRole(session.user.id);
                    const assignedEvents = await fetchAssignedEvents(role);
                    if (mounted) {
                        initializedRef.current = true;
                        updateState({ user: session.user, role, assignedEvents, loading: false });
                    }
                } else {
                    if (mounted) {
                        initializedRef.current = true;
                        updateState({ user: null, role: null, assignedEvents: [], loading: false });
                    }
                }
            } else if (event === 'SIGNED_IN') {
                // Only process SIGNED_IN if we've already initialized (real login action)
                // Skip if not initialized yet - INITIAL_SESSION will handle it
                if (initializedRef.current) {
                    if (session?.user) {
                        const role = await fetchUserRole(session.user.id);
                        const assignedEvents = await fetchAssignedEvents(role);
                        if (mounted) {
                            updateState({ user: session.user, role, assignedEvents, loading: false });
                        }
                    }
                }
                // else: SIGNED_IN before INITIAL_SESSION — let INITIAL_SESSION handle it
            } else if (event === 'TOKEN_REFRESHED') {
                if (session?.user) {
                    // Role/assignments don't change on a token refresh — preserve them.
                    const currentRole = stateRef.current.role;
                    const currentAssigned = stateRef.current.assignedEvents;
                    updateState({ user: session.user, role: currentRole || 'user', assignedEvents: currentAssigned || [], loading: false });
                }
            } else if (event === 'SIGNED_OUT') {
                updateState({ user: null, role: null, assignedEvents: [], loading: false });
            }
        });

        // Handle tab visibility for auto-refresh
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                await supabase.auth.startAutoRefresh();
            } else {
                await supabase.auth.stopAutoRefresh();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            mounted = false;
            subscription.unsubscribe();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            supabase.auth.stopAutoRefresh();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = async (email, password) => {
        updateState({ loading: true, error: null });
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
            updateState({ loading: false, error });
            throw error;
        }
        return data;
    };

    const logout = async () => {
        await supabase.auth.signOut();
        updateState({ user: null, role: null, assignedEvents: [] });
    };

    const assignedEvents = state.assignedEvents || [];

    const value = {
        user: state.user,
        role: state.role,
        assignedEvents,
        // Lowercased event codes an event_admin may access (for URL guards).
        assignedEventCodes: assignedEvents.map(e => (e.code || '').toLowerCase()),
        // Event UUIDs an event_admin may access (for list filtering).
        assignedEventIds: assignedEvents.map(e => e.id),
        loading: state.loading,
        error: state.error,
        login,
        logout,
        isAuthenticated: !!state.user,
        isSuperAdmin: state.role === 'super_admin',
        isEventAdmin: state.role === 'event_admin',
        // isAdmin keeps its original meaning (full-access admin). event_admin is
        // intentionally NOT included here so existing is_admin-style checks are unchanged.
        isAdmin: state.role === 'admin' || state.role === 'super_admin',
        // Any role allowed into the /admin/* shell.
        canAccessAdmin: state.role === 'admin' || state.role === 'super_admin' || state.role === 'event_admin'
    };

    return (
        <AuthContext.Provider value={value}>
            {state.loading ? (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Securely connecting...</p>
                    </div>
                </div>
            ) : (
                children
            )}
        </AuthContext.Provider>
    );
};
